import * as React from 'react';
import s from './solution-rate-reviews.scss';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import {
  getIsEligibleForReviews,
  getReviewesBySolutionId,
} from '../../../../api/api';
import type { ISolutionRateProps } from './solution-rate/solution-rate';
import {
  SolutionRate,
  dtoToSolutionRateProps,
} from './solution-rate/solution-rate';
import {
  dtoToSolutionReviews,
  SolutionReviews,
} from './solution-reviews/solution-reviews';
import { NoReviews } from '.';
import { ReviewsSortBy } from '../../../../enums/marketplace-enums';
import type { GUID } from '@wix/bi-logger-market/v2/types';
import { biLogger } from '../../../../bi/logger';
import {
  appMarketAppPageReadMoreReviewsClick,
  appMarketSortReviews,
} from '@wix/bi-logger-market/v2';
import { withExperiments } from '../../../../component-decorators/with-experiments';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import type {
  ISolutionReview,
  ISolutionReviewsDTO,
} from '../../../../types/common/marketplace';

export interface ISolutionRateReviewsContainerProps
  extends Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  solutionId: string;
  developerName: string;
  developerIcon: string;
  showEmptyState?: boolean;
  appName?: string;
  isMobile: boolean;
  isWixOffering?: boolean;
}

interface IRateReviewsContainerState {
  isFetching: boolean;
  solutionRateChartsProps?: ISolutionRateProps;
  reviews?: ISolutionReview[];
  reviewsOffset: number;
  showDeletedText?: boolean;
  editMode?: { userReview: ISolutionReview; shouldDisplay: boolean };
  sortReviewsBy?: string;
  numberOfReviews?: number;
  noReviews?: boolean;
  endOfReviews?: boolean;
  isEligibleForReviews?: boolean;
  biData?: {
    appId: GUID;
    esi: GUID;
    market: string;
    value: string;
    vertical: string;
  };
}

@withMarketplaceStore
@withExperiments
export class SolutionRateReviewsContainer extends React.Component<
  ISolutionRateReviewsContainerProps,
  IRateReviewsContainerState
> {
  constructor(props) {
    super(props);
    this.state = {
      isFetching: false,
      reviewsOffset: 0,
      endOfReviews: false,
      sortReviewsBy: ReviewsSortBy.MostRecent,
      reviews: [],
    };
    this.loadMoreReviews = this.loadMoreReviews.bind(this);
    this.getReviewsBySortMethod = this.getReviewsBySortMethod.bind(this);
    this.initialize = this.initialize.bind(this);
  }

  render() {
    const { solutionRateChartsProps } = this.state;
    return (
      <div data-testid="rate-and-reviews">
        {this.state.isFetching ? null : this.state.noReviews ||
          this.props.showEmptyState ? (
          <NoReviews
            isEligibleForReviews={
              this.state.isEligibleForReviews || this.props.isWixOffering
            }
            appId={this.props.solutionId}
          />
        ) : (
          <div className={s.solutionRateReviewsContainer} id="reviews">
            <SolutionRate {...this.state.solutionRateChartsProps} />
            <SolutionReviews
              averageRating={
                solutionRateChartsProps && solutionRateChartsProps.reviewStars
              }
              appName={this.props.appName}
              afterPostRender={() =>
                this.initialize(
                  this.state.reviewsOffset + 1,
                  ReviewsSortBy.MostRecent,
                )
              }
              editMode={this.state.editMode}
              showDeletedLink={this.state.showDeletedText}
              loadMore={this.loadMoreReviews}
              sortBy={this.getReviewsBySortMethod}
              reviews={this.state.reviews}
              hideLoadMoreBtn={this.state.endOfReviews}
              developerName={this.props.developerName}
              developerIcon={this.props.developerIcon}
              numberOfReviews={this.state.numberOfReviews}
              isEligibleForReviews={
                this.state.isEligibleForReviews || this.props.isWixOffering
              }
              appId={this.props.solutionId}
            />
          </div>
        )}
      </div>
    );
  }

  async componentDidMount() {
    if (!this.props.showEmptyState) {
      this.props.solutionId && this.initialize();
    }
  }

  async componentDidUpdate(prevProps) {
    if (
      prevProps !== this.props &&
      this.props.solutionId &&
      prevProps.solutionId &&
      prevProps.solutionId !== this.props.solutionId
    ) {
      this.initialize();
    }
  }

  async initialize(limit = 5, afterPostSort?) {
    const solutionId = this.props.solutionId;
    const offset = 0;
    const sortBy = afterPostSort || this.state.sortReviewsBy;

    const [reviewsDto, isEligibleForReviews] = await Promise.all([
      getReviewesBySolutionId(solutionId, sortBy, offset, limit),
      getIsEligibleForReviews(solutionId),
    ]).catch((error) => [[], true]);

    if (reviewsDto && reviewsDto?.body?.reviews?.length) {
      this.setState((prevState) => {
        const newReviews = dtoToSolutionReviews(reviewsDto);
        return {
          noReviews: reviewsDto?.body?.reviews?.length === 0,
          isFetching: false,
          editMode: shouldShowEditLink(reviewsDto),
          showDeletedText: shouldShowDeleteLink(reviewsDto),
          endOfReviews: limit >= reviewsDto.body.totalReviews,
          solutionRateChartsProps: dtoToSolutionRateProps(reviewsDto),
          reviews: newReviews,
          numberOfReviews: reviewsDto.body.totalReviews,
          reviewsOffset: Math.min(limit, reviewsDto.body.totalReviews),
          isEligibleForReviews,
        };
      });
    } else {
      this.setState({
        noReviews: true,
        isEligibleForReviews,
      });
    }
  }

  getReviewsBySortMethod = async (sortMethod: string) => {
    const solutionId = this.props.solutionId;
    const limit = 5;
    const offset = 0;
    const sortBy = sortMethod;

    try {
      const reviewsDto: ISolutionReviewsDTO = await getReviewesBySolutionId(
        solutionId,
        sortBy,
        offset,
        limit,
      );
      reviewsDto?.body?.reviews?.length &&
        this.setState({
          endOfReviews: limit >= reviewsDto.body.totalReviews,
          reviews: dtoToSolutionReviews(reviewsDto),
          reviewsOffset: limit,
          sortReviewsBy: sortMethod,
        });
      await this.sendSortClickedBi(solutionId, sortMethod);
    } catch (error) {
      // console.log(error);
    }
  };

  async loadMoreReviews() {
    const solutionId = this.props.solutionId;
    const limit = 5;
    const offset = this.state.reviewsOffset;
    const sortBy = this.state.sortReviewsBy;
    await biLogger.report(
      appMarketAppPageReadMoreReviewsClick({
        app_id: solutionId,
        tag_name: 'app_page',
      }),
    );
    try {
      const res = await getReviewesBySolutionId(
        solutionId,
        sortBy,
        offset,
        limit,
      );
      const reviewsDto: ISolutionReviewsDTO = res;
      reviewsDto?.body?.reviews?.length &&
        this.setState((prevState) => {
          const newReviews = dtoToSolutionReviews(reviewsDto);
          return {
            endOfReviews:
              prevState.reviewsOffset + limit >= reviewsDto.body.totalReviews,
            reviews: prevState.reviews.concat(newReviews),
            reviewsOffset: prevState.reviewsOffset + limit,
          };
        });
    } catch (error) {
      //   console.log(error);
    }
  }

  async sendSortClickedBi(solutionId: string, sortMethod: string) {
    return biLogger.report(
      appMarketSortReviews({
        appId: solutionId,
        value: biParamsRenaming(sortMethod),
      }),
    );
  }
}

const biParamsRenaming = (key: string): string => {
  return key === ReviewsSortBy.MostHelpful
    ? 'most_helpful'
    : key === ReviewsSortBy.MostRecent
    ? 'most_recent'
    : key === ReviewsSortBy.Rating
    ? 'most_rated'
    : '';
};
const shouldShowDeleteLink = (reviewDto: ISolutionReviewsDTO): boolean => {
  const userReview: ISolutionReview =
    reviewDto.body && reviewDto.body.userReview;
  return userReview && !userReview.publishedAt;
};
const shouldShowEditLink = (
  reviewDto: ISolutionReviewsDTO,
): { userReview: ISolutionReview; shouldDisplay: boolean } => {
  const userReview: ISolutionReview =
    reviewDto.body && reviewDto.body.userReview;
  const shouldDisplay: boolean =
    userReview && !!userReview && !!userReview.publishedAt;
  return {
    userReview,
    shouldDisplay,
  };
};
